import { useState, useEffect } from "react";
import { GiSpades, GiHearts } from "react-icons/gi";
import { socket } from "../../api";
import { parseJWT } from "../../utils/jwtUtils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function CreateGame() {
  const [selected, setSelected] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [privateRoomCode, setPrivateRoomCode] = useState(null);
  const [countdown, setCountdown] = useState(null);

  // ✅ SAMPLE BET AMOUNTS (STATIC)
  const amounts = [10, 20, 30, 50, 100];

  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;

  const userInfo = parseJWT(token?.token);
  // =========================
  // Room Created Toast
  // =========================
  useEffect(() => {
    const handleSuccess = ({ message, newBalance, roomCode }) => {
      toast.success(message || "Room created!", {
        position: "top-center",
        autoClose: 2000,
      });

      if (newBalance !== undefined) {
        localStorage.setItem("balance", newBalance);
      }

      if (isPrivate && roomCode) {
        setPrivateRoomCode(roomCode);
        setCountdown(120);
      }
    };

    const handleFailed = ({ reason, currentBalance, required }) => {
      if (reason === "Insufficient balance") {
        toast.error(
          `Insufficient balance! You have ${currentBalance} Birr, need ${required} Birr.`,
          { position: "top-center", autoClose: 3000 },
        );
      } else {
        toast.error(reason || "Room creation failed!", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    };

    socket.on("room-created-success", handleSuccess);
    socket.on("room-create-failed", handleFailed);

    return () => {
      socket.off("room-created-success", handleSuccess);
      socket.off("room-create-failed", handleFailed);
    };
  }, [isPrivate]);

  // =========================
  // PRIVATE ROOM EVENTS
  // =========================
  useEffect(() => {
    socket.on("room-created", ({ roomCode }) => {
      if (isPrivate) {
        setPrivateRoomCode(roomCode);
        setCountdown(120); // 2 minutes
      }
    });

    socket.on("room-timeout", () => {
      setPrivateRoomCode(null);
      setCountdown(null);
      alert("Room expired. Bet refunded.");
    });

    return () => {
      socket.off("room-created");
      socket.off("room-timeout");
    };
  }, [isPrivate]);

  // =========================
  // COUNTDOWN TIMER
  // =========================
  useEffect(() => {
    if (!countdown) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // =========================
  // CREATE ROOM
  // =========================
  const handleCreate = () => {
    // console.log("Sending isPrivate:", isPrivate);
    if (!selected) return alert("Select bet amount");

    socket.emit("create-room", {
      phone: userInfo.phone,
      name: userInfo.username,
      betAmount: selected,
      isPrivate: isPrivate,
      gameType: "crazy",
    });
  };

  return (
    <div className="w-full max-w-md mx-auto bg-[#134454] rounded-xl shadow-2xl overflow-hidden border border-[#185163]">
      {/* HEADER */}
      <div className="bg-gradient-to-l from-cyan-900 via-cyan-800 to-cyan-900 px-5 py-4 flex items-center gap-3">
        <GiSpades className="text-white text-lg animate-pulse" />
        <h2 className="font-semibold text-white text-lg font-serif">
          Create Game
        </h2>
      </div>

      {/* BET AMOUNTS */}
      <div className="p-6 grid grid-cols-3 gap-3">
        {amounts.map((amount) => (
          <button
            key={amount}
            onClick={() => setSelected(amount)}
            className={`py-3 rounded-lg font-semibold transition border ${
              selected === amount
                ? "bg-gradient-to-r from-primary to-[#6784a4] text-white shadow-lg border-transparent"
                : "bg-[#0c3745] text-gray-300 border-[#185163] hover:bg-[#185163]"
            }`}
          >
            {amount} ETB
          </button>
        ))}
      </div>

      {/* PRIVATE TOGGLE */}
      <div className="mx-6 mb-6 bg-[#0c3745]/80 rounded-2xl p-5 flex justify-between items-center border border-[#185163]">
        <div className="flex items-center gap-3">
          <GiHearts className="text-red-400 text-lg animate-pulse" />
          <span className="text-gray-200 font-medium text-lg">
            Private Game
          </span>
        </div>

        <button
          onClick={() => setIsPrivate(!isPrivate)}
          className={`w-14 h-7 rounded-full p-1 transition-all duration-300 ${
            isPrivate ? "bg-cyan-600 shadow-lg" : "bg-gray-600"
          }`}
        >
          <div
            className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-all duration-300 ${
              isPrivate ? "translate-x-7" : ""
            }`}
          />
        </button>
      </div>

      {/* PRIVATE ROOM DISPLAY */}
      {privateRoomCode && (
        <div className="mx-6 mb-6 bg-[#102f3a] p-5 rounded-xl text-center border border-cyan-700">
          <p className="text-gray-300">Share this room code:</p>
          <p className="text-3xl font-bold text-cyan-400 tracking-widest mt-2">
            {privateRoomCode}
          </p>
          {countdown && (
            <p className="text-sm text-red-400 mt-3">Expires in {countdown}s</p>
          )}
        </div>
      )}

      {/* CREATE BUTTON */}
      <div className="p-6 pt-0">
        <button
          onClick={handleCreate}
          className="w-full py-3 rounded-lg bg-gradient-to-r from-primary to-[#6784a4] hover:opacity-90 text-white font-semibold flex items-center justify-center gap-2 shadow-xl transition"
        >
          <GiSpades />
          {isPrivate ? "Create Private Room" : "Create Public Room"}
        </button>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
}
